import { useMemo } from "react";
import { fetchOverview } from "../api";
import { usePolledData } from "../hooks/usePolledData";
import type { TimeWindow, OverviewResponse } from "../types";
import { fmtMs, fmtNum, fmtPct } from "../format";
import { KpiCard } from "../components/KpiCard";
import { Section } from "../components/Section";
import { DataTable, type ColumnDef } from "../components/DataTable";
import { ApiTypeTag } from "../components/Badges";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Legend 
} from 'recharts';
import type {
  ApiBreakdownRow,
  BrandBreakdownRow,
} from "../types";

interface Props {
  window: TimeWindow;
  onSelectBrand: (brandId: string) => void;
  registerState: (state: {
    loading: boolean;
    lastUpdated: Date | null;
    refetch: () => void;
  }) => void;
}


export const OverviewPage = ({ window, onSelectBrand, registerState }: Props) => {
  const { data, error, loading, lastUpdated, refetch } = usePolledData<OverviewResponse>(
    () => fetchOverview(window),
    { intervalMs: 15_000, deps: [window] },
  );

  registerState({ loading, lastUpdated, refetch });

  const apiCols = useMemo<ColumnDef<ApiBreakdownRow>[]>(
    () => [
      {
        key: "api",
        header: "API",
        render: (r) => <ApiTypeTag type={r._id} />,
      },
      { key: "total", header: "Hits", align: "right", render: (r) => fmtNum(r.total) },
      { key: "ok", header: "OK", align: "right", render: (r) => fmtNum(r.success) },
      { key: "err", header: "Err", align: "right", render: (r) => fmtNum(r.errors) },
      {
        key: "succ",
        header: "Success%",
        align: "right",
        render: (r) => fmtPct(r.total ? (r.success / r.total) * 100 : 0),
      },
      {
        key: "cache",
        header: "Cache%",
        align: "right",
        render: (r) => fmtPct(r.total ? (r.cache_hits / r.total) * 100 : 0),
      },
      {
        key: "ups",
        header: "Upstream",
        align: "right",
        render: (r) => fmtNum(r.upstream_calls),
      },
      {
        key: "avg",
        header: "Avg",
        align: "right",
        render: (r) => fmtMs(r.avg_response_time_ms),
      },
    ],
    [],
  );

  const brandCols = useMemo<ColumnDef<BrandBreakdownRow>[]>(
    () => [
      {
        key: "brand",
        header: "Brand",
        render: (r) => (
          <button
            className="link"
            type="button"
            onClick={() => onSelectBrand(r._id)}
            title={`Drill into ${r._id}`}
          >
            {r._id || "UNKNOWN"}
          </button>
        ),
      },
      { key: "total", header: "Hits", align: "right", render: (r) => fmtNum(r.total) },
      { key: "ok", header: "OK", align: "right", render: (r) => fmtNum(r.success) },
      { key: "err", header: "Err", align: "right", render: (r) => fmtNum(r.errors) },
      {
        key: "succ",
        header: "Success%",
        align: "right",
        render: (r) => fmtPct(r.total ? (r.success / r.total) * 100 : 0),
      },
      {
        key: "cache",
        header: "Cache%",
        align: "right",
        render: (r) => fmtPct(r.total ? (r.cache_hits / r.total) * 100 : 0),
      },
      {
        key: "avg",
        header: "Avg",
        align: "right",
        render: (r) => fmtMs(r.avg_response_time_ms),
      },
    ],
    [onSelectBrand],
  );

  const pieData = useMemo(() => [
    { name: 'Success', value: data?.totals.success || 0 },
    { name: 'Errors', value: data?.totals.errors || 0 },
  ], [data]);

  const sourceData = useMemo(() => 
    data?.by_data_source.map(ds => ({
      name: ds._id || 'Unknown',
      count: ds.count
    })) || [], [data]);

  if (error) {
    return (
      <div className="error-banner">
        <strong>Failed to load overview:</strong> {error.message}
      </div>
    );
  }

  const totals = data?.totals;
  const rates = data?.rates;
  const timing = data?.timing;

  return (
    <>
      <Section title="Aggregate" description={`Over the last ${window}`}>
        <div className="grid grid-4">
          <KpiCard
            label="Total Requests"
            value={fmtNum(totals?.total_requests)}
            sub={`upstream: ${fmtNum(totals?.upstream_calls)}`}
          />
          <KpiCard
            label="Success Rate"
            tone="good"
            value={fmtPct(rates?.success_pct, 2)}
            sub={`${fmtNum(totals?.success)} / ${fmtNum(totals?.total_requests)}`}
          />
          <KpiCard
            label="Error Rate"
            tone={rates?.error_pct ? "bad" : "default"}
            value={fmtPct(rates?.error_pct, 2)}
            sub={`${fmtNum(totals?.errors)} failures`}
          />
          <KpiCard
            label="Cache Hit Rate"
            tone="info"
            value={fmtPct(rates?.cache_hit_pct, 2)}
            sub={`${fmtNum(totals?.cache_hits)} hits`}
          />
        </div>

        <div className="grid grid-3 grid-margin">
          <KpiCard label="Avg Response" value={fmtMs(timing?.avg_response_time_ms)} />
          <KpiCard label="Max Response" value={fmtMs(timing?.max_response_time_ms)} />
          <KpiCard label="Avg Upstream" value={fmtMs(timing?.avg_upstream_ms)} />
        </div>
      </Section>

      <div className="grid grid-2 grid-margin">
        <Section title="Request Distribution">
          <div style={{ width: '100%', height: 300, background: 'var(--bg-2)', borderRadius: 'var(--radius)', padding: '20px' }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={pieData}
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  <Cell fill="var(--green)" />
                  <Cell fill="var(--red)" />
                </Pie>
                <Tooltip 
                  contentStyle={{ background: 'var(--bg-3)', border: '1px solid var(--line)', color: 'var(--fg)' }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Section>
        <Section title="Source Breakdown">
          <div style={{ width: '100%', height: 300, background: 'var(--bg-2)', borderRadius: 'var(--radius)', padding: '20px' }}>
            <ResponsiveContainer>
              <BarChart data={sourceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                <XAxis dataKey="name" stroke="var(--fg-dim)" fontSize={10} />
                <YAxis stroke="var(--fg-dim)" fontSize={10} />
                <Tooltip 
                  contentStyle={{ background: 'var(--bg-3)', border: '1px solid var(--line)', color: 'var(--fg)' }}
                />
                <Bar dataKey="count" fill="var(--cyan)" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>

      <Section title="Per-API breakdown">
        <DataTable
          columns={apiCols}
          rows={data?.by_api_type}
          rowKey={(r) => String(r._id)}
          loading={loading}
        />
      </Section>

      <Section title="Per-Brand breakdown" description="Click a brand to drill in.">
        <DataTable
          columns={brandCols}
          rows={data?.by_brand}
          rowKey={(r) => r._id}
          loading={loading}
        />
      </Section>
    </>
  );
};
